import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getLatestUrlValidation } from '../services/rpc';
import { supabase } from '../utils/supabaseClient';
import type { URLValidatorResult } from '../types/url-validation';

export const urlValidationQueryKey = (programId: string) => ['url-validation', programId] as const;

interface ValidateUrlsParams {
  seedUrls?: string[];
  forceRevalidate?: boolean;
}

interface ValidateUrlsResponse {
  status: 'success' | 'error';
  validation?: URLValidatorResult;
  error?: string;
}

/**
 * Hook for managing URL validation state and actions
 */
export function useUrlValidation(programId: string | null) {
  const queryClient = useQueryClient();
  const [urlsDirty, setUrlsDirty] = useState(false);

  // Query for latest validation result
  const validationQuery = useQuery<URLValidatorResult | null>({
    queryKey: programId ? urlValidationQueryKey(programId) : ['url-validation', 'noop'],
    queryFn: () => {
      if (!programId) {
        return Promise.resolve(null);
      }
      return getLatestUrlValidation(programId);
    },
    enabled: Boolean(programId),
    staleTime: 5 * 60 * 1000 // 5 minutes
  });

  // Mutation for running validation via Edge Function
  const validateMutation = useMutation<ValidateUrlsResponse, Error, ValidateUrlsParams>({
    mutationFn: async ({ seedUrls, forceRevalidate }) => {
      if (!programId) {
        throw new Error('Program ID is required to validate URLs');
      }

      const { data, error } = await supabase.functions.invoke('qc-validate-urls', {
        body: {
          program_id: programId,
          seed_urls: seedUrls,
          force_revalidate: forceRevalidate
        }
      });

      if (error) {
        throw error;
      }

      return data as ValidateUrlsResponse;
    },
    onSuccess: (response) => {
      if (programId && response.validation) {
        // Update query cache with new validation
        queryClient.setQueryData(urlValidationQueryKey(programId), response.validation);
        setUrlsDirty(false);
      }
    }
  });

  // Subscribe to realtime changes (for multi-user scenarios)
  useEffect(() => {
    if (!programId) return;

    const channel = supabase
      .channel(`url-validation:${programId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'prod',
          table: 'activity_logs',
          filter: `program_id=eq.${programId}`
        },
        (payload) => {
          const details = payload.new?.details as Record<string, unknown>;
          if (details?.url_validation) {
            // Another user/tab ran validation - refresh
            void queryClient.invalidateQueries({ queryKey: urlValidationQueryKey(programId) });
          }
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [programId, queryClient]);

  return {
    // Query state
    validation: validationQuery.data,
    isLoading: validationQuery.isLoading,
    error: validationQuery.error,

    // Validation actions
    runValidation: validateMutation.mutateAsync,
    validating: validateMutation.isPending,
    validationError: validateMutation.error,

    // Refetch
    refetch: validationQuery.refetch,

    // URL dirty state
    urlsDirty,
    markUrlsDirty: () => setUrlsDirty(true),
    markUrlsClean: () => setUrlsDirty(false)
  };
}
